import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";

/**
 * Wyszukiwanie firm z promptu.
 *
 * Zlecenie liczy się w tle, więc widok odpytuje serwer co kilka sekund dopóki
 * trwa — i przestaje, gdy tylko się skończy. Bez tego użytkownik patrzy na
 * „w trakcie" i sam odświeża stronę.
 */

export type SearchStatus = "pending" | "running" | "done" | "failed";
export type CandidateStatus = "proposed" | "accepted" | "rejected";

export interface SearchCandidate {
  id: string;
  name: string;
  nip: string;
  nip_verified: boolean;
  city: string;
  voivodeship: string;
  voivodeship_label: string;
  website: string;
  email: string;
  phone: string;
  contact_person: string;
  rationale: string;
  sources: string[];
  status: CandidateStatus;
  status_label: string;
  lead: string | null;
  existing_lead: string | null;
  existing_lead_name: string | null;
  existing_contractor: string | null;
  existing_contractor_name: string | null;
  is_suppressed: boolean;
  is_blocked: boolean;
  enrichment_status: "none" | "pending" | "running" | "done" | "failed";
  enrichment_log: string;
  /** Skąd wzięła się każda doszukana dana — rejestr, KRS albo konkretna strona. */
  field_sources: Record<string, { origin: "registry" | "krs" | "web"; label: string; url?: string }>;
  enriched_at: string | null;
  /** Z której rundy pochodzi kandydat: 0 = pierwotne pytanie, n = n-te dopytanie. */
  round: number;
}

/** Jedna runda dopytania w zleceniu; bez `finished_at` — jeszcze się liczy. */
export interface SearchRound {
  prompt: string;
  asked_at: string;
  asked_by?: string;
  max_results?: number;
  started_at?: string;
  finished_at?: string;
  found?: number;
  cost_usd?: string;
  web_searches?: number;
  model?: string;
  error?: string;
}

export interface LeadSearchRow {
  id: string;
  prompt: string;
  /** Dopytania w tym zleceniu — kolejne pytania dopisują kandydatów do tej samej listy. */
  rounds: SearchRound[];
  /** Historyczne: dopytania sprzed rund były osobnymi zleceniami. */
  parent: string | null;
  parent_prompt: string | null;
  refinements: { id: string; prompt: string; status: SearchStatus; count: number }[];
  voivodeships: string[];
  max_results: number;
  project: string | null;
  project_name: string | null;
  status: SearchStatus;
  status_label: string;
  /** Wpis w rejestrze zadań w tle (/bg-tasks) ostatniego przebiegu; null dla zleceń sprzed rejestru. */
  task_id: string | null;
  error: string;
  started_at: string | null;
  finished_at: string | null;
  model_used: string;
  web_searches: number;
  cost_usd: string;
  counts: { total: number; proposed: number; accepted: number; rejected: number };
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface LeadSearchDetail extends LeadSearchRow {
  /** Tok pracy modelu: frazy wyszukiwania i streszczone rozumowanie. Tylko na karcie —
   *  w liście (odpytywanej co 5 s) ważyłby do 40 000 znaków na wiersz. */
  log: string;
  /** Nazwa celowo NIE „results": globalny renderer API traktuje taki klucz jak
   *  odpowiedź stronicowaną i odsyła samą listę zamiast całej karty zlecenia. */
  candidates: SearchCandidate[];
}

const KEY = ["leads", "searches"];

export function useLeadSearches() {
  return useQuery({
    queryKey: KEY,
    queryFn: () =>
      apiClient.get<{ data: LeadSearchRow[] }>("/leads/searches/").then((r) => r.data.data),
    // Lista pokazuje też zlecenia w trakcie — odświeżamy, dopóki któreś liczy.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((row) => row.status === "pending" || row.status === "running")
        ? 5000
        : false,
  });
}

export function useLeadSearch(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, id],
    enabled: !!id,
    queryFn: () =>
      apiClient
        .get<{ data: LeadSearchDetail }>(`/leads/searches/${id}/`)
        .then((r) => r.data.data),
    refetchInterval: (query) => {
      const data = query.state.data;
      const running = data?.status === "pending" || data?.status === "running";
      // Doszukiwanie też leci w tle — bez tego użytkownik patrzy na „w trakcie"
      // i sam odświeża stronę.
      const enriching = (data?.candidates ?? []).some(
        (row) => row.enrichment_status === "pending" || row.enrichment_status === "running",
      );
      return running || enriching ? 4000 : false;
    },
  });
}

function useSearchMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Akceptacja kandydata tworzy leada — lista leadów i liczniki też się zmieniają.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });
}

export function useCreateSearch() {
  return useSearchMutation(
    (payload: { prompt: string; voivodeships: string[]; max_results: number }) =>
      apiClient
        .post<{ data: LeadSearchDetail }>("/leads/searches/", payload)
        .then((r) => r.data.data),
  );
}

export interface AcceptOutcome {
  /** `email` = adres, który REALNIE wszedł na kartę firmy. Pusty znaczy, że kampania
   *  tę firmę pominie; `warning` pojawia się, gdy kandydat miał coś, co adresem nie
   *  było (brak „@") i zostało odrzucone. */
  created: { id: string; name: string; email: string; warning?: string }[];
  skipped: { id: string; name: string; reason: string }[];
  /** Id, które w ogóle nie weszły do rozpatrzenia — obce albo już rozpatrzone. */
  ignored: { id: string; reason: string }[];
}

export function useAcceptCandidates() {
  return useSearchMutation(({ searchId, resultIds }: { searchId: string; resultIds: string[] }) =>
    apiClient
      .post<{ data: AcceptOutcome }>(`/leads/searches/${searchId}/accept/`, {
        result_ids: resultIds,
      })
      .then((r) => r.data.data),
  );
}

export function useRejectCandidates() {
  return useSearchMutation(({ searchId, resultIds }: { searchId: string; resultIds: string[] }) =>
    apiClient.post(`/leads/searches/${searchId}/reject/`, { result_ids: resultIds }),
  );
}

export function useDeleteSearch() {
  return useSearchMutation((id: string) => apiClient.delete(`/leads/searches/${id}/`));
}

/** Dopytanie — kolejna runda w TYM SAMYM zleceniu: nowi kandydaci dopisują się do
 *  listy, dotychczasowi zostają. Zlecenie wraca do „oczekuje" i widok znów odpytuje. */
export function useRefineSearch() {
  return useSearchMutation(
    ({ id, prompt, maxResults }: { id: string; prompt: string; maxResults?: number }) =>
      apiClient
        .post<{ data: LeadSearchDetail }>(`/leads/searches/${id}/refine/`, {
          prompt,
          ...(maxResults ? { max_results: maxResults } : {}),
        })
        .then((r) => r.data.data),
  );
}

/** Powtórzenie zlecenia — kasuje nieprzejrzanych kandydatów i pyta jeszcze raz. */
/** Doszukanie danych zaznaczonych firm — NIP, mail, telefon, osoba z KRS. */
export function useEnrichCandidates() {
  return useSearchMutation(({ searchId, resultIds }: { searchId: string; resultIds: string[] }) =>
    apiClient.post(`/leads/searches/${searchId}/enrich/`, { result_ids: resultIds }),
  );
}

export function useRerunSearch() {
  return useSearchMutation((id: string) => apiClient.post(`/leads/searches/${id}/rerun/`, {}));
}

export interface CandidatePatch {
  name?: string;
  nip?: string;
  city?: string;
  voivodeship?: string;
  website?: string;
  email?: string;
  phone?: string;
  contact_person?: string;
  rationale?: string;
}

/** Ręczna poprawka kandydata. Serwer przelicza po niej weryfikację NIP i kolizje. */
export function useUpdateCandidate() {
  return useSearchMutation(({ id, ...patch }: CandidatePatch & { id: string }) =>
    apiClient
      .patch<{ data: SearchCandidate }>(`/leads/search-results/${id}/`, patch)
      .then((r) => r.data.data),
  );
}
